import { create } from 'zustand';
import { leadService, Lead } from '../services/leadService';
import { useAuthStore } from './authStore';

interface LeadStore {
  leads: Lead[];
  unassignedLeads: Lead[];
  myLeads: Lead[];
  isLoading: boolean;
  fetchLeads: () => Promise<void>;
  assignLeadToMe: (leadId: string, agentName: string) => Promise<void>;
  assignLeadToAgent: (leadId: string, agentName: string) => Promise<void>;
  updateLeadStage: (leadId: string, stage: Lead['stage']) => Promise<void>;
  closeLead: (leadId: string) => Promise<void>;
  initializeLeads: () => void;
}

export const useLeadStore = create<LeadStore>((set, get) => ({
  leads: [],
  unassignedLeads: [],
  myLeads: [],
  isLoading: false,

  fetchLeads: async () => {
    set({ isLoading: true });
    try {
      const leads = await leadService.getLeads();

      // Dashboard unassigned = Only AI-processed leads with positive interest
      // Exclude: potential leads, not_interested leads, lost leads, and assigned leads
      // Include: qualified leads and unassigned leads with genuine interest
      const unassigned = leads.filter(l => {
        // Filter out leads with negative outcomes using backend status
        const isNegativeOutcome = ['not_interested', 'lost', 'potential'].includes(l.originalStatus || '');

        // Only show as unassigned if stage is 'unassigned' or 'new' (but not 'assigned')
        // AND there's no assignedTo value
        return (l.stage === 'unassigned' || (l.stage === 'new' && !l.assignedTo)) &&
               l.aiSummary !== 'Potential lead - Not yet contacted' &&
               !isNegativeOutcome &&
               !l.assignedTo;
      });

      // Get current user from auth store to properly filter assigned leads
      const authState = useAuthStore.getState();
      const currentUser = authState.user;
      const myLeads = leads.filter(l =>
        l.assignedTo === currentUser?.name ||
        l.assignedTo === 'Demo Agent' // Also check for Demo Agent as fallback
      );

      set({
        leads,
        unassignedLeads: unassigned,
        myLeads,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      set({ isLoading: false });
    }
  },

  assignLeadToMe: async (leadId: string, agentName: string) => {
    const lead = get().unassignedLeads.find(l => l.id === leadId);
    if (!lead) return;

    const updatedLead = await leadService.assignLead(leadId, agentName);
    if (updatedLead) {
      // Instead of updating local state, fetch fresh data from backend
      // This ensures we get the correct stage and assignment info
      await get().fetchLeads();
    }
  },

  assignLeadToAgent: async (leadId: string, agentName: string) => {
    const lead = get().unassignedLeads.find(l => l.id === leadId);
    if (!lead) return;

    const updatedLead = await leadService.assignLead(leadId, agentName);
    if (updatedLead) {
      set((state) => ({
        unassignedLeads: state.unassignedLeads.filter(l => l.id !== leadId),
        leads: state.leads.map(l => l.id === leadId ? updatedLead : l),
      }));
    }
  },

  updateLeadStage: async (leadId: string, stage: Lead['stage']) => {
    // Optimistic update - immediately update the UI
    set((state) => ({
      myLeads: state.myLeads.map(lead =>
        lead.id === leadId ? { ...lead, stage } : lead
      ),
      leads: state.leads.map(lead =>
        lead.id === leadId ? { ...lead, stage } : lead
      ),
    }));

    // Then make the API call
    const updatedLead = await leadService.updateLeadStage(leadId, stage);

    // If the API call succeeds, update with the server response
    // If it fails, this will revert the optimistic update
    if (updatedLead) {
      set((state) => ({
        myLeads: state.myLeads.map(lead =>
          lead.id === leadId ? updatedLead : lead
        ),
        leads: state.leads.map(lead =>
          lead.id === leadId ? updatedLead : lead
        ),
      }));
    } else {
      // If the API call fails, revert the change by fetching fresh data
      get().fetchLeads();
    }
  },

  closeLead: async (leadId: string) => {
    const success = await leadService.closeLead(leadId);
    if (success) {
      set((state) => ({
        myLeads: state.myLeads.filter(lead => lead.id !== leadId),
        leads: state.leads.filter(lead => lead.id !== leadId),
      }));
    }
  },

  initializeLeads: () => {
    // Fetch leads from API on initialization
    get().fetchLeads();
  },
}));